/**
 * Utility functions for authentication
 * Handles platform-specific redirect URIs for OAuth
 */

import { supabase } from '../supabase';

/**
 * Get Supabase redirect URI for OAuth callbacks
 * 
 * For Capacitor apps (Android/iOS):
 * - Use deep link scheme: com.ronaldoroy.shikhunitalian://callback
 * - Supabase will redirect to this after OAuth completes
 * 
 * For Web:
 * - Use standard redirect: window.location.origin/home
 */
export const getSupabaseRedirectURI = (): string => {
  // Check if running in Capacitor (Android/iOS native app)
  if (typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform()) {
    const platform = (window as any).Capacitor.getPlatform();
    
    if (platform === 'android' || platform === 'ios') {
      // Use deep link scheme for native apps
      // This must match the scheme in AndroidManifest.xml
      // Format: scheme://host
      const appId = 'com.ronaldoroy.shikhunitalian';
      const deepLink = `${appId}://callback`;
      console.log('🔐 Using deep link redirect for', platform, ':', deepLink);
      return deepLink;
    }
  }
  
  // For web browsers, use the standard redirect
  const webRedirect = `${window.location.origin}/home`;
  console.log('🔐 Using web redirect:', webRedirect);
  return webRedirect;
};

/**
 * Handle OAuth callback from deep link
 * Called when app receives a deep link after OAuth redirect
 */
export const handleOAuthCallback = async (url: string): Promise<boolean> => {
  try {
    console.log('🔗 Handling OAuth callback - Full URL:', url);
    console.log('🔗 URL length:', url.length);
    
    // Check if this is an OAuth callback
    if (!url || (!url.includes('callback') && !url.includes('#') && !url.includes('access_token'))) {
      console.log('⚠️ Not an OAuth callback URL - missing callback/hash/access_token');
      return false;
    }
    
    // First, try to let Supabase handle it automatically by setting the hash in window.location
    // This works for web, but for native apps we need to extract manually
    if (typeof window !== 'undefined' && window.location && url.includes('#')) {
      const hashMatch = url.match(/#([^#]*)$/);
      if (hashMatch && hashMatch[1]) {
        const hash = hashMatch[1];
        console.log('🔗 Found hash fragment:', hash.substring(0, 50) + '...');
        
        // Extract tokens from hash for both web and native apps
        const params = new URLSearchParams(hash);
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        const error = params.get('error');
        
        console.log('🔗 Extracted from hash:', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          hasError: !!error,
          accessTokenLength: accessToken?.length || 0,
          refreshTokenLength: refreshToken?.length || 0,
        });
        
        if (error) {
          console.error('❌ OAuth error:', error, params.get('error_description'));
          return false;
        }
        
        if (accessToken && refreshToken) {
          console.log('✅ Both tokens found, setting session...');
          
          // Set the session with the tokens
          const { data: { session }, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          
          if (sessionError) {
            console.error('❌ setSession error:', sessionError);
            console.error('❌ Error message:', sessionError.message);
            console.error('❌ Error status:', sessionError.status);
            return false;
          }
          
          if (session && session.user) {
            console.log('✅ Session established successfully!');
            console.log('✅ User email:', session.user.email);
            console.log('✅ User ID:', session.user.id);
            return true;
          } else {
            console.error('❌ Session was null or missing user after setSession');
            return false;
          }
        } else {
          console.error('❌ Missing tokens - accessToken:', !!accessToken, 'refreshToken:', !!refreshToken);
          return false;
        }
      }
    }
    
    // Fallback: Try parsing as URL
    try {
      const urlObj = new URL(url);
      const hash = urlObj.hash.substring(1);
      
      if (hash) {
        const params = new URLSearchParams(hash);
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        
        if (accessToken && refreshToken) {
          console.log('✅ Tokens found (URL parsing fallback)');
          const { data: { session }, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          
          if (sessionError) {
            console.error('❌ setSession error (fallback):', sessionError);
            return false;
          }
          
          return !!session;
        }
      }
      
      // Check query params
      const accessToken = urlObj.searchParams.get('access_token');
      const refreshToken = urlObj.searchParams.get('refresh_token');
      if (accessToken && refreshToken) {
        console.log('✅ Tokens found in query params');
        const { data: { session }, error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        return !sessionError && !!session;
      }
    } catch (urlError) {
      console.warn('⚠️ URL parsing failed:', urlError);
    }
    
    console.error('❌ Failed to extract tokens from URL');
    return false;
  } catch (error) {
    console.error('❌ Error handling OAuth callback:', error);
    if (error instanceof Error) {
      console.error('❌ Error message:', error.message);
      console.error('❌ Error stack:', error.stack);
    }
    return false;
  }
};

